import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export type PublicWorkspaceBranding = {
  dateFormat?: string | null;
  timeZone?: string | null;
  orgId: string | null;
  orgName: string;
  logoUrl: string | null;
  coverImageUrl: string | null;
  primaryColor: string;
  accentColor: string;
  portalSlug: string | null;
  primaryDomain: string | null;
  currency: string | null;
  locale: string | null;
};

const DEFAULT_BRANDING: PublicWorkspaceBranding = {
  orgId: null,
  orgName: 'Apex Habitat',
  logoUrl: null,
  coverImageUrl: null,
  primaryColor: '#2FC1F1',
  accentColor: '#35C0EF',
  portalSlug: null,
  primaryDomain: null,
  currency: null,
  locale: null,
};

const PUBLIC_BRANDING_STORAGE_KEY = 'apexPublicWorkspaceBranding.v2';
const LEGACY_PLATFORM_COLORS = new Set(['#2FC1F1', '#35C0EF']);

@Injectable({ providedIn: 'root' })
export class PublicWorkspaceBrandingService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly base = `${environment.apiGatewayUrl}/property-service`;
  private loadKey: string | null = null;
  private loadPromise: Promise<PublicWorkspaceBranding> | null = null;

  readonly branding = signal<PublicWorkspaceBranding>(DEFAULT_BRANDING);
  readonly loading = signal(false);
  readonly requestedWorkspace = signal<string | null>(null);
  readonly workspaceLookupFailed = signal(false);

  load(routeSnapshot?: ActivatedRouteSnapshot | null): void {
    void this.resolve(routeSnapshot);
  }

  async resolve(routeSnapshot?: ActivatedRouteSnapshot | null): Promise<PublicWorkspaceBranding> {
    const routeHint = this.brandingFromRoute(routeSnapshot);
    if (routeHint) {
      const next = this.normalizeBranding({
        ...this.branding(),
        ...routeHint,
      });
      this.branding.set(next);
      this.applyTheme(next);
    }

    const workspace = this.workspaceFromRoute(routeSnapshot) || this.workspaceFromBrowser();
    return this.resolveWorkspaceSlug(workspace);
  }

  async resolveWorkspaceSlug(workspace?: string | null): Promise<PublicWorkspaceBranding> {
    workspace = workspace?.trim() || null;
    this.requestedWorkspace.set(workspace);
    this.applyCachedBranding(workspace);
    if (!workspace) {
      this.applyTheme(this.branding());
      return this.branding();
    }

    if (workspace === this.loadKey) {
      if (this.loadPromise) {
        return this.loadPromise;
      }

      this.applyTheme(this.branding());
      return this.branding();
    }

    this.loadKey = workspace;
    this.loading.set(true);

    this.loadPromise = this.fetchWorkspaceBranding(workspace);

    try {
      return await this.loadPromise;
    } finally {
      this.loadPromise = null;
      this.loading.set(false);
    }
  }

  applyCachedOrgBranding(orgId?: string | null): boolean {
    const key = orgId?.trim();
    if (!key || !isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      const raw = window.localStorage.getItem(PUBLIC_BRANDING_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      const cached = parsed?.byOrg?.[key];
      if (!cached?.primaryColor) {
        return false;
      }

      const next = this.normalizeBranding({
        ...this.branding(),
        ...cached,
      });
      this.branding.set(next);
      this.applyTheme(next);
      return true;
    } catch {
      return false;
    }
  }

  private async fetchWorkspaceBranding(workspace: string): Promise<PublicWorkspaceBranding> {
    try {
      const branding = await firstValueFrom(
        this.http.get<Partial<PublicWorkspaceBranding>>(
          `${this.base}/api/v1/public/workspaces/${encodeURIComponent(workspace)}/branding`,
          { withCredentials: true },
        ),
      );
      const next = this.normalizeBranding(branding);
      this.branding.set(next);
      this.workspaceLookupFailed.set(!next.orgId);
      this.applyTheme(next);
      this.rememberBranding(workspace, next);
      return next;
    } catch {
      const next = this.normalizeBranding(this.branding());
      this.branding.set(next);
      this.workspaceLookupFailed.set(true);
      this.applyTheme(next);
      return next;
    }
  }

  async requestWorkspaceLookup(email: string): Promise<void> {
    await firstValueFrom(
      this.http.post(
        `${this.base}/api/v1/public/workspaces/lookup`,
        { email: email.trim().toLowerCase() },
        { withCredentials: true },
      ),
    );
  }

  workspaceFromRoute(routeSnapshot?: ActivatedRouteSnapshot | null): string | null {
    const direct =
      routeSnapshot?.queryParamMap.get('workspace') ||
      routeSnapshot?.queryParamMap.get('org') ||
      routeSnapshot?.queryParamMap.get('workspaceSlug');

    if (direct?.trim()) {
      return direct.trim();
    }

    const returnUrl =
      routeSnapshot?.queryParamMap.get('returnUrl') ||
      routeSnapshot?.queryParamMap.get('redirect') ||
      routeSnapshot?.queryParamMap.get('redirectTo');

    return this.workspaceFromUrl(returnUrl);
  }

  private brandingFromRoute(routeSnapshot?: ActivatedRouteSnapshot | null): Partial<PublicWorkspaceBranding> | null {
    const primaryColor = this.normalizeColor(
      routeSnapshot?.queryParamMap.get('brandPrimary') || routeSnapshot?.queryParamMap.get('primaryColor'),
      '',
    );
    const accentColor = this.normalizeColor(
      routeSnapshot?.queryParamMap.get('brandAccent') || routeSnapshot?.queryParamMap.get('accentColor'),
      primaryColor || '',
    );

    if (!primaryColor && !accentColor) {
      return null;
    }

    return {
      primaryColor: primaryColor || undefined,
      accentColor: accentColor || primaryColor || undefined,
    };
  }

  isGenericPublicHost(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      const currentHost = window.location.hostname.toLowerCase();
      const publicHost = new URL(environment.publicAppUrl).hostname.toLowerCase();
      return !this.isLocalHost(currentHost) && currentHost === publicHost;
    } catch {
      return false;
    }
  }

  isBrandedPortalHost(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    return this.workspaceFromBrowser() !== null;
  }

  brandedLoginUrl(workspace: string): string {
    const slug = workspace.trim().toLowerCase();

    try {
      const url = new URL(environment.publicAppUrl);
      if (this.isLocalHost(url.hostname)) {
        url.pathname = '/login';
        url.search = '';
        url.searchParams.set('workspace', slug);
        return url.toString();
      }

      if (!url.hostname.startsWith(`${slug}.`)) {
        url.hostname = `${slug}.${url.hostname}`;
      }

      url.pathname = '/login';
      url.search = '';
      return url.toString();
    } catch {
      return `/login?workspace=${encodeURIComponent(slug)}`;
    }
  }

  loginHostSuffix(): string {
    try {
      const host = new URL(environment.publicAppUrl).hostname.toLowerCase();
      if (this.isLocalHost(host)) {
        return '.apex-habitat.com';
      }

      return `.${host.replace(/^www\./, '')}`;
    } catch {
      return '.apex-habitat.com';
    }
  }

  private workspaceFromBrowser(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    return this.workspaceFromHost(window.location.hostname);
  }

  private workspaceFromUrl(value?: string | null): string | null {
    if (!value || !isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      const url = new URL(value, window.location.origin);
      return this.workspaceFromHost(url.hostname);
    } catch {
      return null;
    }
  }

  private workspaceFromHost(hostname: string): string | null {
    const host = hostname.toLowerCase();
    if (!host || this.isLocalHost(host)) {
      return null;
    }

    const publicPortalHost = this.environmentHost(environment.publicAppUrl);
    if (publicPortalHost && host.endsWith(`.${publicPortalHost}`)) {
      const slug = host.slice(0, -publicPortalHost.length - 1).split('.').filter(Boolean).at(-1);
      return slug || null;
    }

    const portalHosts = [
      'backoffice-dev.apex-habitat.com',
      'backoffice.apex-habitat.com',
      'tenant-dev.apex-habitat.com',
      'tenant.apex-habitat.com',
      'owner-dev.apex-habitat.com',
      'owner.apex-habitat.com',
      'platform-admin-dev.apex-habitat.com',
      'platform-admin.apex-habitat.com',
      'admin-dev.apex-habitat.com',
      'admin.apex-habitat.com',
    ];

    for (const portalHost of portalHosts) {
      if (host.endsWith(`.${portalHost}`)) {
        const slug = host.slice(0, -portalHost.length - 1).split('.').filter(Boolean).at(-1);
        return slug || null;
      }
    }

    if (host.endsWith('.apex-habitat.com')) {
      return null;
    }

    return host;
  }

  private environmentHost(value: string): string | null {
    try {
      return new URL(value).hostname.toLowerCase();
    } catch {
      return null;
    }
  }

  private isLocalHost(host: string): boolean {
    return host === 'localhost'
      || host.endsWith('.localhost')
      || /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
  }

  private normalizeBranding(branding: Partial<PublicWorkspaceBranding> | null | undefined): PublicWorkspaceBranding {
    const primaryColor = this.normalizeColor(branding?.primaryColor, DEFAULT_BRANDING.primaryColor);
    const accentColor = this.normalizeColor(branding?.accentColor, primaryColor);
    return {
      ...DEFAULT_BRANDING,
      ...branding,
      orgName: branding?.orgName?.trim() || DEFAULT_BRANDING.orgName,
      logoUrl: branding?.logoUrl || null,
      primaryColor,
      accentColor,
    };
  }

  private applyTheme(branding: PublicWorkspaceBranding): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const root = this.document.documentElement;
    root.style.setProperty('--workspace-primary', branding.primaryColor);
    root.style.setProperty('--workspace-accent', branding.accentColor);
    root.style.setProperty('--workspace-secondary', branding.accentColor);
    root.style.setProperty('--primary-color', branding.primaryColor);
    root.style.setProperty('--primary', branding.primaryColor);
  }

  private applyCachedBranding(workspace: string | null): void {
    if (!workspace || !isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const raw = window.localStorage.getItem(PUBLIC_BRANDING_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      const key = workspace.trim().toLowerCase();
      const cached = parsed?.byWorkspace?.[key] || parsed?.byOrg?.[key];
      if (!cached?.primaryColor) {
        return;
      }

      const next = this.normalizeBranding({
        ...this.branding(),
        ...cached,
      });
      this.branding.set(next);
      this.applyTheme(next);
    } catch {
      // Cached branding only prevents refresh flicker; live branding still works without storage.
    }
  }

  private rememberBranding(workspace: string, branding: PublicWorkspaceBranding): void {
    if (!branding.orgId || !isPlatformBrowser(this.platformId)) {
      return;
    }

    const key = workspace.trim().toLowerCase();
    const orgId = branding.orgId.trim();

    try {
      const raw = window.localStorage.getItem(PUBLIC_BRANDING_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      const byWorkspace = parsed && typeof parsed === 'object' && parsed.byWorkspace && typeof parsed.byWorkspace === 'object'
        ? parsed.byWorkspace
        : {};
      const byOrg = parsed && typeof parsed === 'object' && parsed.byOrg && typeof parsed.byOrg === 'object'
        ? parsed.byOrg
        : {};
      const cached = {
        orgId,
        orgName: branding.orgName,
        logoUrl: branding.logoUrl,
        coverImageUrl: branding.coverImageUrl,
        primaryColor: branding.primaryColor,
        accentColor: branding.accentColor,
        currency: branding.currency,
        locale: branding.locale,
      };

      window.localStorage.setItem(PUBLIC_BRANDING_STORAGE_KEY, JSON.stringify({
        lastWorkspace: key || parsed?.lastWorkspace || null,
        lastOrgId: orgId || parsed?.lastOrgId || null,
        byWorkspace: key ? { ...byWorkspace, [key]: cached } : byWorkspace,
        byOrg: orgId ? { ...byOrg, [orgId]: cached } : byOrg,
      }));
    } catch {
      // Cached branding only prevents refresh flicker; live branding still works without storage.
    }
  }

  private normalizeColor(value: string | null | undefined, fallback: string): string {
    const color = value?.trim();
    if (!color || !/^#[0-9A-Fa-f]{6}$/.test(color)) {
      return fallback;
    }

    const normalized = color.toUpperCase();
    return LEGACY_PLATFORM_COLORS.has(normalized) ? fallback : color;
  }
}
